// bag-radial.ts — single-click an item in your own FT3D inventory windows (bag / on hand / equipment) → the action
// wheel with Drop and Throw (owner 2026-09-16). The verbs are ftInventoryVerbActions (throw-object.ts), the same set
// the creature radial's "Held items" shows, so the two cannot disagree:
//   · anything not already in hand comes out through a FREE hand — Drop needs one, Throw draws it into one;
//   · in combat a throw from the hand is ⚡ (1 AP), from the bag ⚡⚡ (the draw + the throw); Janus charges both.
//
// It must never fight drag-and-drop. DragManager starts a drag only after 6 px of travel from mousedown, so the wheel
// opens on the pointer RELEASE of a press that never left that radius, and waits out the double-click window so a
// double-click still opens the item card (template.js handleGlobalTap) without a wheel flashing first.

import { SceneObjectActionMenu, type SceneObjectAction } from '../encounter-creation-engine/scene-object-action-menu';
import { resolveCachedItemForRender } from './find-cached-item';
import { inventoryField, itemsCol } from '../shared/array-types';
import { casterHoldsRow, ftEnsureWeaponThrowable, ftInventoryVerbActions, throwObjectPlan } from './throw-object';

/** DragManager's click-vs-drag radius (refreshDragAndDrop). */
const DRAG_THRESHOLD_PX = 6;
/** template.js DOUBLE_CLICK_DELAY is 200 ms; a little slack so the second click always lands first. */
const DOUBLE_CLICK_WAIT_MS = 230;
const PANES = '[data-ft-pc-pane="bag_window"], [data-ft-pc-pane="onhand_window"], [data-ft-pc-pane="equipment_window"]';

type Row = Record<string, unknown>;
const host = window as Window & {
    ftGetActiveCasterId?: () => string;
    FateTide?: { casterCache?: Map<string, Row> };
    aoeManager?: { ft3dActive?: unknown };
    dragManager?: { state?: { isDragging?: boolean } };
    ftInventoryRadialThumbnail?: (id: string) => { imageUrl: string | null };
    openItemInfo?: (id: string) => void;
};

const menu = new SceneObjectActionMenu();
/** A touch double tap: the second valid tap on the same row inside this window. Mouse and pen use the browser's own
 *  double-click recognition (click.detail), which follows the user's OS setting. */
const DOUBLE_TAP_WINDOW_MS = 400;

/** The ONE pointer being tracked (its id and type), from press to release; `moved` latches past the drag radius, so a
 *  drag dropped back on its origin is never a tap. `pressId` is the row the PRESS itself landed on (never a control). */
let gesture: { pointerId: number; pointerType: string; x: number; y: number; pressId: string; moved: boolean } | null = null;
/** Every pointer currently down. Overlapping contacts void tap recognition until ALL of them have lifted. */
const down = new Map<number, string>();
let overlapped = false;
/** A completed tap waiting out the double-click window — an immutable record; a new press or a double click cancels it. */
let pending: number | null = null;
/** Bumped by every press, so a wheel whose weapon lookup is still out never opens over a newer gesture. */
let openSeq = 0;
/** The last valid TOUCH tap (touch double taps only); any other completed gesture resets it. */
let lastTouchTap: { id: string; at: number } | null = null;
/** Last few open/skip decisions — read by the staging smoke when a wheel does not open (window.ftBagRadialTrace). */
const trace: string[] = [];
function note(decision: string): void {
    trace.push(decision);
    if (trace.length > 12) trace.shift();
}

function cancelPending(): void {
    if (pending !== null) window.clearTimeout(pending);
    pending = null;
}

/** The inventory tile a press / release on an FT3D inventory window lands on, or null when this gesture is not ours. */
function tileFor(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    // Buttons inside a tile (status-effect chips, qty controls) keep their own clicks.
    if (target.closest('button, a, input, select, textarea, [role="button"], [onclick]')) return null;
    const tile = target.closest<HTMLElement>('.inventory-item[data-inventory_id]');
    if (!tile || !tile.closest(PANES) || !tile.closest('[data-ft3d-campaign-encounter-view]')) return null;
    return tile;
}

/** The current node for a row in a pane kind — a caster_update repaint can swap the tile inside the wait. */
function findTile(id: string, pane: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(`[data-ft3d-campaign-encounter-view] [data-ft-pc-pane="${CSS.escape(pane)}"] .inventory-item[data-inventory_id="${CSS.escape(id)}"]`);
}

function busy(tile: HTMLElement): boolean {
    return !!(host.dragManager?.state?.isDragging || tile.classList.contains('dragging') || host.aoeManager?.ft3dActive);
}

/** A mouse or pen is one device: a press from it means its earlier release was lost (let go outside the window). */
function forgetStale(pointerType: string): void {
    if (pointerType === 'touch') return;
    for (const [id, type] of down) if (type === pointerType) down.delete(id);
}
// Losing the window loses every release that follows it.
window.addEventListener('blur', () => { down.clear(); overlapped = false; gesture = null; lastTouchTap = null; cancelPending(); openSeq += 1; });

function lift(pointerId: number): void {
    down.delete(pointerId);
    if (down.size === 0) overlapped = false;
}

document.addEventListener('pointerdown', event => {
    cancelPending();
    openSeq += 1;
    forgetStale(event.pointerType);
    if (down.size === 0) overlapped = false;
    down.set(event.pointerId, event.pointerType);
    if (down.size > 1) {
        // A second contact (a pinch, a touch held while the mouse clicks): no tap until every contact has lifted.
        overlapped = true;
        gesture = null;
        lastTouchTap = null;
        note('skip:multi-pointer');
        return;
    }
    if (!event.isPrimary || event.button !== 0) return;
    const pressTile = tileFor(event.target);
    // A press on an inventory tile while a wheel is open closes the wheel FIRST — before the wheel's own outside-press
    // guard (registered later, at open time; a listener removed mid-dispatch is skipped) can swallow the press and
    // starve DragManager's mousedown. Presses inside the wheel are left to the wheel.
    if (menu.isOpen && pressTile) menu.close();
    gesture = pressTile
        ? { pointerId: event.pointerId, pointerType: event.pointerType, x: event.clientX, y: event.clientY, pressId: String(pressTile.dataset.inventory_id ?? ''), moved: false }
        : null;
    if (!pressTile) lastTouchTap = null;
}, true);
document.addEventListener('pointermove', event => {
    if (!gesture || event.pointerId !== gesture.pointerId || gesture.moved) return;
    if (Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > DRAG_THRESHOLD_PX) gesture.moved = true;
}, true);
// The browser took the gesture over (a scroll / pan): never a tap, and it breaks a double-tap sequence.
document.addEventListener('pointercancel', event => {
    lift(event.pointerId);
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    gesture = null;
    lastTouchTap = null;
}, true);

document.addEventListener('pointerup', event => {
    const wasOverlapped = overlapped;
    lift(event.pointerId);
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const g = gesture;
    gesture = null; // movement after the release never touches this tap
    // Touch retargets to the pressed element; a mouse or pen release reports what is under the pointer.
    const target = g.pointerType === 'touch' ? document.elementFromPoint(event.clientX, event.clientY) : event.target;
    const tile = tileFor(target);
    const chord = event.shiftKey || event.ctrlKey || event.altKey || event.metaKey; // wares / ping chords
    // The press and the release must both be on the SAME row's tile (a press that began on a control is never a tap).
    const sameRow = !!tile && String(tile.dataset.inventory_id ?? '') === g.pressId;
    if (!tile || !sameRow || wasOverlapped || chord || g.moved || Math.hypot(event.clientX - g.x, event.clientY - g.y) > DRAG_THRESHOLD_PX || busy(tile)) {
        note(`skip:release tile=${!!tile} sameRow=${sameRow} overlapped=${wasOverlapped} moved=${g.moved} chord=${chord}`);
        lastTouchTap = null;
        return;
    }
    const id = g.pressId;
    const pane = tile.closest<HTMLElement>('[data-ft-pc-pane]')?.dataset.ftPcPane ?? '';
    const now = Date.now();
    if (g.pointerType === 'touch') {
        if (lastTouchTap && lastTouchTap.id === id && now - lastTouchTap.at < DOUBLE_TAP_WINDOW_MS) {
            lastTouchTap = null;
            note('skip:double-tap');
            // DragManager's touchstart preventDefault suppresses the click template.js opens the card from: open it here.
            host.openItemInfo?.(id);
            return;
        }
        lastTouchTap = { id, at: now };
    } else {
        lastTouchTap = null;
    }
    const at = { x: event.clientX, y: event.clientY };
    pending = window.setTimeout(() => {
        pending = null;
        const live = tile.isConnected ? tile : findTile(id, pane);
        if (!live || busy(live)) { note(`skip:timer live=${!!live}`); return; }
        // A carried weapon's throwability may need one server answer first (tens of ms, cached per row): open after it,
        // unless another press started meanwhile.
        const opened = ++openSeq;
        void ftEnsureWeaponThrowable(id).then(() => {
            if (opened !== openSeq) { note('skip:superseded'); return; }
            const current = live.isConnected ? live : findTile(id, pane);
            if (current && !busy(current)) openBagRadial(current, at);
        });
    }, DOUBLE_CLICK_WAIT_MS);
}, true);

// Mouse / pen double click, as the BROWSER recognises it (the OS interval): the click with detail ≥ 2 on a tile cancels
// the wheel the second release armed and closes one the first release already opened; template.js opens the card.
// Window capture runs before any document handler can stop the click.
window.addEventListener('click', event => {
    if (event.detail < 2 || !tileFor(event.target)) return;
    cancelPending();
    if (menu.isOpen) menu.close();
    note('skip:double-click');
}, true);

function openBagRadial(tile: HTMLElement, client: { x: number; y: number }): void {
    const id = String(tile.dataset.inventory_id ?? '').trim().toUpperCase();
    const casterId = String(host.ftGetActiveCasterId?.() ?? '').trim();
    const caster = casterId ? host.FateTide?.casterCache?.get(casterId) ?? host.FateTide?.casterCache?.get(casterId.toUpperCase()) : undefined;
    // Only YOUR things: a looted corpse, a GM peek or another sheet's window keeps the drag-only behaviour.
    if (!id || !caster || !casterHoldsRow(caster, id)) { note(`skip:not-yours caster=${!!caster}`); return; }
    const row = resolveCachedItemForRender(id) as Row | null;
    if (!row) { note('skip:no-row'); return; }
    note('open');
    const name = String(inventoryField(row, 'inventory_name') ?? itemsCol(row, 'item_name') ?? 'Item');
    const plan = throwObjectPlan(id);
    const state = plan.held && plan.mode === 'throw' ? 'In hand' : 'Carried';
    const actions: SceneObjectAction[] = [
        ...ftInventoryVerbActions(casterId, id).map(action => ({ ...action, run: () => {
            // A wheel is a snapshot: never act for a character that control has since moved to.
            if (String(host.ftGetActiveCasterId?.() ?? '').toUpperCase() !== casterId.toUpperCase()) return;
            action.run();
        } })),
        { label: 'Details', icon: 'info', reason: 'Open the item card', run: () => { host.openItemInfo?.(id); } },
    ];
    menu.open(name, state, client, actions, tile, host.ftInventoryRadialThumbnail?.(id) ?? undefined);
}

Object.assign(window, { ftBagRadialTrace: trace, ftOpenBagRadial: (inventoryId: string, x = window.innerWidth / 2, y = window.innerHeight / 2): boolean => {
    const tile = document.querySelector<HTMLElement>(`${PANES.split(', ').map(pane => `${pane} .inventory-item[data-inventory_id="${CSS.escape(inventoryId)}"]`).join(', ')}`);
    if (!tile) return false;
    openBagRadial(tile, { x, y });
    return menu.isOpen;
} });
